import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Maximize2, 
  Minimize2, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  BookOpen, 
  FileText,
  RotateCcw
} from 'lucide-react';

const PAGES = [
  {
    num: 1,
    title: "Forside",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s1.png"
  },
  {
    num: 2,
    title: "Side 2",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s2.png"
  },
  {
    num: 3,
    title: "Side 3",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s3.png"
  },
  {
    num: 4,
    title: "Side 4",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s4.png"
  },
  {
    num: 5,
    title: "Side 5",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s5.png"
  },
  {
    num: 6,
    title: "Side 6",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s6.png"
  },
  {
    num: 7,
    title: "Side 7",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s7.png"
  },
  {
    num: 8,
    title: "Bagside",
    url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s8.png"
  }
];

const PDF_URL = "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/a5-folder-medimismart-til-hjemmesiden-og-kunne-bladres-i.pdf";

const FolderFlipbook = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [direction, setDirection] = useState('next'); // 'next' or 'prev' for animations
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);

  // Handle window resize to switch layouts
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe handlers for mobile/tablet flipping
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }
  };

  // Sync zoom level when exiting fullscreen
  useEffect(() => {
    if (!isFullscreen) {
      setZoomLevel(1);
    }
  }, [isFullscreen]);

  // Navigation helpers
  const totalPages = PAGES.length;

  const handleNext = () => {
    setDirection('next');
    if (isDesktop) {
      if (currentPage === 1) {
        setCurrentPage(2);
      } else if (currentPage + 2 <= totalPages) {
        setCurrentPage(currentPage + 2);
      } else if (currentPage + 1 <= totalPages) {
        setCurrentPage(currentPage + 1);
      }
    } else {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    }
  };

  const handlePrev = () => {
    setDirection('prev');
    if (isDesktop) {
      if (currentPage <= 3) {
        setCurrentPage(1);
      } else {
        setCurrentPage(currentPage - 2);
      }
    } else {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    }
  };

  const goToPage = (pageNum) => {
    setDirection(pageNum > currentPage ? 'next' : 'prev');
    if (isDesktop) {
      if (pageNum === 1) {
        setCurrentPage(1);
      } else {
        // Align to even page start for spreads (2-3, 4-5, etc.)
        setCurrentPage(pageNum % 2 === 0 ? pageNum : pageNum - 1);
      }
    } else {
      setCurrentPage(pageNum);
    }
  };

  // Get current spread pages
  const getSpreadPages = () => {
    if (!isDesktop) {
      return [PAGES[currentPage - 1]];
    }
    if (currentPage === 1) {
      return [PAGES[0]]; // Front cover centered
    }
    if (currentPage === totalPages) {
      return [PAGES[totalPages - 1]]; // Back cover centered
    }
    const leftPage = PAGES[currentPage - 1];
    const rightPage = currentPage < totalPages ? PAGES[currentPage] : null;
    return [leftPage, rightPage].filter(Boolean);
  };

  const activePages = getSpreadPages();

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, isDesktop, isFullscreen]);

  // Helper to get optimized image URL
  const getOptimizedUrl = (url, width = 800) => {
    return `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=90,format=auto/${url.replace('https://assets.auxodev.dk/', '')}`;
  };

  return (
    <div className="w-full py-12 px-4 bg-slate-50 border-y border-slate-200">
      <div className="max-w-5xl mx-auto">
        
        {/* Header Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-light mb-1">
              <BookOpen size={14} /> Interaktiv Brochure
            </span>
            <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              Bladr i vores Medimi®Smart A5 Folder
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              Klik på siderne eller brug pilene til at bladre. Du kan også åbne i fuld skærm for nemmere læsning.
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setIsFullscreen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-lg text-xs font-semibold shadow-sm transition-all"
              title="Åbn i fuld skærm"
            >
              <Maximize2 size={14} />
              Fuld skærm
            </button>
            <a
              href={PDF_URL}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand text-white hover:bg-brand-dark rounded-lg text-xs font-semibold shadow-sm transition-all"
            >
              <Download size={14} />
              Download PDF
            </a>
          </div>
        </div>

        {/* Main Flipbook Container */}
        <div 
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="relative flex flex-col items-center justify-center min-h-[200px] md:min-h-[350px] w-full overflow-visible select-none px-4 md:px-8"
        >
          {/* Book Layout */}
          <div className="relative w-full max-w-5xl flex justify-center items-center select-none">
            
            {/* Left Navigation Arrow */}
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className={`absolute left-2 lg:-left-6 z-40 p-3 rounded-full bg-white/95 hover:bg-white text-slate-900 shadow-lg border border-slate-200 transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95 ${
                currentPage === 1 ? 'pointer-events-none' : ''
              }`}
              aria-label="Forrige side"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Book Body */}
            <div 
              className={`relative flex transition-all duration-500 ease-out ${
                isDesktop && currentPage > 1 && currentPage < totalPages ? 'w-full aspect-[2.828/1]' : 'w-full max-w-xl aspect-[1.414/1]'
              }`}
            >
              {/* Spine Shadow & Binding Effect (Only on desktop spreads) */}
              {isDesktop && currentPage > 1 && currentPage < totalPages && (
                <>
                  {/* Center spine line */}
                  <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-black/10 z-20 transform -translate-x-1/2"></div>
                  {/* Spine shadow left */}
                  <div className="absolute right-1/2 top-0 bottom-0 w-8 bg-gradient-to-r from-transparent to-black/8 z-20 pointer-events-none"></div>
                  {/* Spine shadow right */}
                  <div className="absolute left-1/2 top-0 bottom-0 w-8 bg-gradient-to-l from-transparent to-black/8 z-20 pointer-events-none"></div>
                </>
              )}

              {/* Render Pages */}
              {isDesktop && currentPage > 1 && currentPage < totalPages ? (
                // Desktop Spread View (2 pages side-by-side)
                <div className="w-full h-full grid grid-cols-2 rounded-xl overflow-hidden shadow-2xl border border-slate-300/50 bg-white">
                  {/* Left Page */}
                  <div 
                    onClick={handlePrev}
                    className="relative w-full h-full bg-white border-r border-slate-100 cursor-pointer overflow-hidden group"
                  >
                    <img
                      src={getOptimizedUrl(activePages[0].url, 800)}
                      alt={activePages[0].title}
                      className="w-full h-full object-contain pointer-events-none transition-transform duration-300 group-hover:scale-[1.01]"
                      loading="lazy"
                    />
                    {/* Page Number */}
                    <div className="absolute bottom-3 left-4 text-[10px] font-bold text-slate-400">
                      Side {activePages[0].num}
                    </div>
                    {/* Left page edge shadow */}
                    <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-black/5 to-transparent pointer-events-none"></div>
                  </div>

                  {/* Right Page */}
                  <div 
                    onClick={activePages[1] ? handleNext : undefined}
                    className={`relative w-full h-full bg-white cursor-pointer overflow-hidden group ${
                      !activePages[1] ? 'pointer-events-none' : ''
                    }`}
                  >
                    {activePages[1] ? (
                      <>
                        <img
                          src={getOptimizedUrl(activePages[1].url, 800)}
                          alt={activePages[1].title}
                          className="w-full h-full object-contain pointer-events-none transition-transform duration-300 group-hover:scale-[1.01]"
                          loading="lazy"
                        />
                        {/* Page Number */}
                        <div className="absolute bottom-3 right-4 text-[10px] font-bold text-slate-400">
                          Side {activePages[1].num}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-slate-50 flex items-center justify-center">
                        <span className="text-xs text-slate-300">Tom side</span>
                      </div>
                    )}
                    {/* Right page edge shadow */}
                    <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                </div>
              ) : (
                // Mobile or Cover View (Single Page centered)
                <div 
                  onClick={currentPage === 1 ? handleNext : handleNext}
                  className="w-full h-full bg-white rounded-xl overflow-hidden shadow-2xl border border-slate-300/50 cursor-pointer relative group flex items-center justify-center"
                >
                  <img
                    src={getOptimizedUrl(activePages[0].url, 1000)}
                    alt={activePages[0].title}
                    className="w-full h-full object-contain pointer-events-none transition-transform duration-300 group-hover:scale-[1.01]"
                  />
                  {/* Page Number */}
                  <div className="absolute bottom-3 right-4 text-[10px] font-bold text-slate-400">
                    Side {activePages[0].num} af {totalPages}
                  </div>
                  {/* Book cover spine effect on front and back cover */}
                  {(currentPage === 1 || currentPage === totalPages) && (
                    <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-black/15 via-black/5 to-transparent border-l border-black/10"></div>
                  )}
                  {/* Soft edge shadows */}
                  <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-l from-black/5 to-transparent pointer-events-none"></div>
                </div>
              )}

            </div>

            {/* Right Navigation Arrow */}
            <button
              onClick={handleNext}
              disabled={isDesktop ? (currentPage + 1 >= totalPages) : (currentPage === totalPages)}
              className={`absolute right-2 lg:-right-6 z-40 p-3 rounded-full bg-white/95 hover:bg-white text-slate-900 shadow-lg border border-slate-200 transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95 ${
                (isDesktop ? (currentPage + 1 >= totalPages) : (currentPage === totalPages)) ? 'pointer-events-none' : ''
              }`}
              aria-label="Næste side"
            >
              <ChevronRight size={20} />
            </button>

          </div>

          {/* Page indicator & Progress Bar */}
          <div className="mt-8 w-full max-w-md flex flex-col items-center gap-3">
            <div className="text-xs font-bold text-slate-600">
              {isDesktop && currentPage > 1 ? (
                <span>Side {currentPage} - {Math.min(currentPage + 1, totalPages)} af {totalPages}</span>
              ) : (
                <span>Side {currentPage} af {totalPages}</span>
              )}
            </div>
            
            {/* Progress bar */}
            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-brand transition-all duration-300"
                style={{ 
                  width: `${((isDesktop && currentPage > 1 && currentPage < totalPages ? currentPage + 1 : currentPage) / totalPages) * 100}%` 
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Thumbnail Navigation Strip */}
        <div className="mt-6">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center md:text-left">
            Hurtig navigation
          </div>
          <div className="flex gap-2.5 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
            {PAGES.map((page) => {
              const isActive = isDesktop 
                ? (currentPage === 1 ? page.num === 1 : (page.num === currentPage || page.num === currentPage + 1))
                : page.num === currentPage;

              return (
                <button
                  key={page.num}
                  onClick={() => goToPage(page.num)}
                  className={`relative shrink-0 w-20 md:w-28 aspect-[1.414/1] rounded-lg overflow-hidden border-2 transition-all ${
                    isActive 
                      ? 'border-brand shadow-md scale-95 ring-2 ring-brand/20' 
                      : 'border-slate-200 hover:border-slate-400 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={getOptimizedUrl(page.url, 200)}
                    alt={page.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/10 hover:bg-transparent transition-colors"></div>
                  <div className="absolute bottom-1 right-1 bg-black/60 text-[8px] font-bold text-white px-1 rounded">
                    {page.num}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {/* Fullscreen / Focus Mode Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-slate-950/95 flex flex-col backdrop-blur-sm animate-fade-in">
          
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-slate-900 border-b border-slate-800 text-white">
            <div className="flex items-center gap-3">
              <FileText className="text-brand-soft" size={18} />
              <div>
                <h4 className="font-bold text-sm md:text-base">Medimi®Smart A5 Folder</h4>
                <p className="text-xs text-slate-400">Fuld skærm & zoom tilstand</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              {/* Zoom Controls */}
              <div className="flex items-center bg-slate-800 rounded-lg p-0.5 border border-slate-700">
                <button
                  onClick={() => setZoomLevel(Math.max(1, zoomLevel - 0.5))}
                  disabled={zoomLevel === 1}
                  className="p-1.5 hover:bg-slate-700 rounded text-slate-300 disabled:opacity-30 disabled:hover:bg-transparent"
                  title="Zoom ud"
                >
                  <ZoomOut size={16} />
                </button>
                <span className="text-xs px-2 font-mono text-slate-300 min-w-[45px] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.5))}
                  disabled={zoomLevel === 3}
                  className="p-1.5 hover:bg-slate-700 rounded text-slate-300 disabled:opacity-30 disabled:hover:bg-transparent"
                  title="Zoom ind"
                >
                  <ZoomIn size={16} />
                </button>
                {zoomLevel > 1 && (
                  <button
                    onClick={() => setZoomLevel(1)}
                    className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                    title="Nulstil zoom"
                  >
                    <RotateCcw size={14} />
                  </button>
                )}
              </div>

              <a
                href={PDF_URL}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-colors"
                title="Download PDF"
              >
                <Download size={16} />
              </a>

              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 bg-brand text-white hover:bg-brand-dark rounded-lg text-xs font-bold transition-colors"
              >
                Luk fuld skærm
              </button>
            </div>
          </div>

          {/* Modal Content / Zoomable View */}
          <div 
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="flex-1 overflow-auto flex items-center justify-center p-4 md:p-8"
          >
            <div 
              className="relative transition-all duration-300 ease-out flex justify-center items-center"
              style={{ 
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                cursor: zoomLevel > 1 ? 'grab' : 'default'
              }}
            >
              <div className={`relative flex ${
                isDesktop && currentPage > 1 && currentPage < totalPages ? 'w-full max-w-7xl aspect-[2.828/1]' : 'w-full max-w-3xl aspect-[1.414/1]'
              }`}>
                
                {/* Spine Shadow on spread */}
                {isDesktop && currentPage > 1 && currentPage < totalPages && (
                  <>
                    <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-black/20 z-20 transform -translate-x-1/2"></div>
                    <div className="absolute right-1/2 top-0 bottom-0 w-12 bg-gradient-to-r from-transparent to-black/15 z-20 pointer-events-none"></div>
                    <div className="absolute left-1/2 top-0 bottom-0 w-12 bg-gradient-to-l from-transparent to-black/15 z-20 pointer-events-none"></div>
                  </>
                )}

                {isDesktop && currentPage > 1 && currentPage < totalPages ? (
                  <div className="w-full h-full grid grid-cols-2 rounded-xl overflow-hidden shadow-2xl bg-white">
                    <div className="relative w-full h-full overflow-hidden">
                      <img
                        src={getOptimizedUrl(activePages[0].url, 1200)}
                        alt={activePages[0].title}
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute bottom-3 left-4 text-[10px] font-bold text-slate-400">
                        Side {activePages[0].num}
                      </div>
                    </div>
                    <div className="relative w-full h-full overflow-hidden">
                      {activePages[1] ? (
                        <>
                          <img
                            src={getOptimizedUrl(activePages[1].url, 1200)}
                            alt={activePages[1].title}
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute bottom-3 right-4 text-[10px] font-bold text-slate-400">
                            Side {activePages[1].num}
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full bg-slate-900 flex items-center justify-center">
                          <span className="text-xs text-slate-500">Tom side</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full bg-white rounded-xl overflow-hidden shadow-2xl relative flex items-center justify-center">
                    <img
                      src={getOptimizedUrl(activePages[0].url, 1600)}
                      alt={activePages[0].title}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute bottom-3 right-4 text-[10px] font-bold text-slate-400">
                      Side {activePages[0].num} af {totalPages}
                    </div>
                    {(currentPage === 1 || currentPage === totalPages) && (
                      <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-black/15 via-black/5 to-transparent border-l border-black/10"></div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal Footer Controls */}
          <div className="bg-slate-900 border-t border-slate-800 px-6 py-4 flex items-center justify-between text-white">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              Forrige
            </button>

            <div className="text-xs font-bold text-slate-400">
              {isDesktop && currentPage > 1 ? (
                <span>Side {currentPage} - {Math.min(currentPage + 1, totalPages)} af {totalPages}</span>
              ) : (
                <span>Side {currentPage} af {totalPages}</span>
              )}
            </div>

            <button
              onClick={handleNext}
              disabled={isDesktop ? (currentPage + 1 >= totalPages) : (currentPage === totalPages)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Næste
              <ChevronRight size={16} />
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default FolderFlipbook;
